import React, { useCallback, useEffect, useState } from 'react';
import { Grid3x3, KeyRound, Tag } from 'lucide-react';
import ProfileMenu from './elements/profileMenu.jsx';
import ConsultantBoard from './elements/consultantBoard.jsx';
import EditProfileModal from './elements/editProfileModal.jsx';
import { useMyDesigns, DesignsGrid } from './elements/myDesigns.jsx';
import { phoneText } from './elements/navBus.jsx';
import './profile.scss';

const MOCK_USER = {
  username: 'ali_rezaei',
  name: 'علی رضایی',
  bio: 'مشاور املاک | تهران، ایران\nخرید، فروش و رهن و اجاره ملک',
  avatarInitial: 'ع',
  stats: { files: 24, views: 1280, contracts: 340 },
};

function ProfilePage({ user, onLogout, onUserUpdate }) {
  const [editOpen, setEditOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [shareMsg, setShareMsg] = useState('');
  // اشتراک‌گذاریِ پروفایل: منوی اشتراک گوشی (اگر باشد) وگرنه کپیِ لینک
  const shareProfile = async () => {
    if (!user) return;
    const url = `${window.location.origin}/p/${user.id}`;
    const title = `${user.firstName} ${user.lastName} · خونه‌رو`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareMsg('لینک پروفایل کپی شد');
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setShareMsg('کپی نشد؛ لینک: ' + url);
    }
    setTimeout(() => setShareMsg(''), 2500);
  };
  // تعدادِ درخواست‌های تازه‌ی کارتابل (بخش‌های بی‌صدا حساب نمی‌شوند) — کنارِ دکمه‌ی کارتابل نشان داده می‌شود
  const [inboxCount, setInboxCount] = useState(0);
  const isConsultant = user?.role === 'consultant';
  const refreshInboxCount = useCallback(() => {
    if (!isConsultant) return;
    fetch('/api/requests/inbox/count', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setInboxCount(d.total || 0))
      .catch(() => {});
  }, [isConsultant]);
  useEffect(() => {
    refreshInboxCount();
    if (!isConsultant) return undefined;
    const timer = setInterval(refreshInboxCount, 60000);
    return () => clearInterval(timer);
  }, [isConsultant, refreshInboxCount]);
  // نام و حرفِ اولِ عکس از حسابِ واقعی؛ بقیه فعلاً نمونه است
  const fullName = user ? `${user.firstName} ${user.lastName}` : MOCK_USER.name;
  const initial = user ? user.firstName.charAt(0) : MOCK_USER.avatarInitial;
  const bio = user ? user.bio || '' : MOCK_USER.bio;
  const [activeTab, setActiveTab] = useState('grid');
  // طرح‌های ذخیره‌شده‌ی خودِ کاربر (رندرها) — تب‌ها: همه / رهن و اجاره / فروش
  const designs = useMyDesigns(user);
  const totalViews = designs.items.reduce((s, d) => s + (d.views || 0), 0);

  return (
    <div className="profile-page">
      <header className="profile-page__header">
        <span className="profile-page__username">{fullName}</span>
        <ProfileMenu onLogout={onLogout} onEdit={user ? () => setEditOpen(true) : undefined} />
      </header>

      <section className="profile-page__top">
        <div className="profile-page__avatar">
          <span>{initial}</span>
        </div>

        <div className="profile-page__stats">
          <div className="profile-page__stat">
            <strong>{user ? designs.items.length.toLocaleString('fa-IR') : MOCK_USER.stats.files}</strong>
            <span>فایل</span>
          </div>
          <div className="profile-page__stat">
            <strong>{(user ? totalViews : MOCK_USER.stats.views).toLocaleString('fa-IR')}</strong>
            <span>بازدید</span>
          </div>
          <div className="profile-page__stat">
            <strong>{MOCK_USER.stats.contracts.toLocaleString('fa-IR')}</strong>
            <span>قرارداد</span>
          </div>
        </div>
      </section>

      <section className="profile-page__bio">
        <strong>{fullName}</strong>
        {bio ? (
          bio.split('\n').map((line, i) => <p key={i}>{line || '\u00a0'}</p>)
        ) : (
          <p className="profile-page__bio-empty">هنوز بیوگرافی ننوشته‌اید — با «ویرایش پروفایل» اضافه کنید.</p>
        )}
        {user?.phone && (
          <p className="profile-page__bio-empty">
            شماره‌ی تماسِ شما (برای مشتری‌ها نمایش داده می‌شود): <span dir="ltr">{phoneText(user.phone)}</span>
          </p>
        )}
      </section>

      <section className="profile-page__actions">
        {user?.role === 'consultant' ? (
          <button type="button" className="profile-page__btn profile-page__btn--badged" onClick={() => setBoardOpen(true)}>
            کارتابل
            {inboxCount > 0 && <span className="profile-page__badge">{inboxCount.toLocaleString('fa-IR')}</span>}
          </button>
        ) : (
          <button type="button" className="profile-page__btn" onClick={() => user && setEditOpen(true)}>
            ویرایش پروفایل
          </button>
        )}
        <button type="button" className="profile-page__btn" onClick={shareProfile} disabled={!user}>
          اشتراک‌گذاری پروفایل
        </button>
        {shareMsg && <p className="profile-page__share-msg">{shareMsg}</p>}
      </section>

      <nav className="profile-page__tabs">
        <button
          type="button"
          className={activeTab === 'grid' ? 'is-active' : ''}
          onClick={() => setActiveTab('grid')}
          aria-label="فایل‌ها"
        >
          <Grid3x3 size={22} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          className={activeTab === 'rent' ? 'is-active' : ''}
          onClick={() => setActiveTab('rent')}
          aria-label="رهن و اجاره"
        >
          <KeyRound size={22} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          className={activeTab === 'sale' ? 'is-active' : ''}
          onClick={() => setActiveTab('sale')}
          aria-label="فروش ملک"
        >
          <Tag size={22} strokeWidth={1.6} />
        </button>
      </nav>

      <DesignsGrid
        className="profile-page__grid"
        designs={designs.items}
        loading={designs.loading}
        error={designs.error}
        onDeleted={designs.remove}
        filter={activeTab === 'grid' ? 'all' : activeTab}
        emptyText={
          activeTab === 'rent'
            ? 'طرحی برای آگهیِ رهن و اجاره ذخیره نکرده‌اید.'
            : activeTab === 'sale'
              ? 'طرحی برای آگهیِ فروش ذخیره نکرده‌اید.'
              : undefined
        }
      />
      {boardOpen && (
        <ConsultantBoard
          onClose={() => {
            setBoardOpen(false);
            refreshInboxCount();
          }}
          onChange={refreshInboxCount}
        />
      )}
      {editOpen && (
        <EditProfileModal
          user={user}
          onClose={() => setEditOpen(false)}
          onSaved={(u) => {
            onUserUpdate?.(u);
            setEditOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default ProfilePage;